import { loadStripe } from "@stripe/stripe-js";
import { keepPreviousData } from "@tanstack/react-query";
import { motion } from "motion/react";
import { toast } from "sonner";
import { ArrowRight, Calendar, CalendarPlus, CalendarX, Check, ChevronLeftIcon, ChevronRightIcon, CloudIcon, Eye, Loader2, MoonIcon, Pencil, SunIcon, type LucideIcon } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, addMonths, subMonths, addDays, startOfDay, differenceInCalendarDays } from "date-fns";
import { trpc } from "~/api/trpc";
import DiscountCodeSection from "~/components/booking-review/DiscountCodeSection";
import MessageSection from "~/components/booking-review/MessageSection";
import PaymentMethodSection, { type PaymentMethod } from "~/components/booking-review/PaymentMethodSection";
import ScrollMoreHint from "~/components/booking-review/ScrollMoreHint";
import ServicesList from "~/components/booking-review/ServicesList";
import SummaryCard from "~/components/booking-review/SummaryCard";
import { GTMEvent } from "~/components/gtmProvider/GTMContext";
import useGTM from "~/components/gtmProvider/useGTM";
import { formatPractitionerName, type PastPractitionerOption, PastPractitionerPickerRow } from "~/components/new/PastPractitionerPicker";
import TopProgressBar from "~/components/new/TopProgressBar";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import CancelRetentionDialog, { DIALOG_CANCEL_BUTTON_CLASSES, REASON_DIALOG_CLASSES } from "~/components/appointment/CancelRetentionDialog";
import ServiceSelection from "~/pages/bookingwidget/ServiceSelection";
import { buttonVariants } from "~/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "~/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import { KLARNA_MIN_CART_TOTAL_EUR } from "~/lib/klarna";
import LocalDateTime from "~/lib/localdatetime";
import { cn } from "~/lib/utils";
import { type CartItem, getCartTotalAfterDiscount, type MatchedDiscountCode } from "~/stores/booking-flow-store";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";
import { AppointmentStatus } from "~/types/server-types/AppointmentStatus";
import { CUSTOMER_CANCELLATION_REASON_LABELS, CUSTOMER_REASON_ICONS, type CustomerCancellationReason, type ReasonLabel } from "~/types/server-types/AppointmentCancellationReason";

type AppointmentCartItem = Omit<CartItem, 'bookableItemDescriptionDe' | 'bookableItemDescriptionEn' | 'selectedVariantOptionLabelDe' | 'selectedVariantOptionLabelEn'>;

type Appointment = {
    id: string;
    title: string;
    bookedPrice: number;
    status: AppointmentStatus;
    messageToCustomer: string | null;
    messageFromCustomer: string | null;
    couponCodes: string | null;
    startTimeLocalSerialized: string;
    endTimeLocalSerialized: string;
    isDoctorAppointment: boolean;
    customer: {
        phone: string | null;
        email: string | null;
        firstName: string;
        lastName: string;
    };
    variants: {
        id: string;
        name: string;
    }[];
    cartItems: AppointmentCartItem[] | null;
    klarnaAuthorization: {
        amountCents: number;
        expiresAt: string | null;
    } | null;
    store: {
        id: number | 'digital';
        name: string;
        address: string;
        zipCode: string;
        fullAddress: string;
        contactPhone: string;
        contactEmail: string;
        reviewsCount: number;
        reviewsStarsOutOfFive: number;
        images: string[];
        clinicGoogleMapsImgSrc: string;
        googleMapsUrl: string;
    };
};

interface Timeslot {
    startTime: LocalDateTime;
    startTimeStr: string;
}

type TimeOfDay = 'morning' | 'afternoon' | 'evening';

type ChangeAppointmentWidgetProps = {
    appointmentId: string;
    storeId: number | 'digital';
    onSelectDate: (date: LocalDateTime | undefined) => void;
    variantIds: string[];
    practitionerId: number | null;
    useRequestVariantIds?: boolean;
    preselectStartTime?: LocalDateTime;
}

const ChangeAppointmentWidget = ({
    appointmentId,
    storeId,
    onSelectDate,
    variantIds,
    practitionerId,
    useRequestVariantIds,
    preselectStartTime,
}: ChangeAppointmentWidgetProps) => {
    const { language, t } = useLanguageStore();

    const [currentMonth, setCurrentMonth] = useState(() => {
        if (!preselectStartTime) return new Date();
        const [year, month] = preselectStartTime.getDisplayDate().split('-').map(Number);
        return new Date(year, (month ?? 1) - 1, 1);
    });
    const [selectedDate, setSelectedDate] = useState<LocalDateTime | null>(null);
    const [selectedTimeOfDay, setSelectedTimeOfDay] = useState<TimeOfDay>('morning');
    const [showMoreTimeslots, setShowMoreTimeslots] = useState(false);
    const [timeslotViewMode, setTimeslotViewMode] = useState<"day-view" | "month-view">('day-view');
    const [selectedTimeslot, setSelectedTimeslot] = useState<LocalDateTime | undefined>(undefined);
    const [originalSlotUnavailable, setOriginalSlotUnavailable] = useState(false);

    const dateScrollContainerRef = useRef<HTMLDivElement>(null);

    const queryParams = useMemo(() => ({
        storeId: storeId,
        from: startOfMonth(currentMonth).toISOString(),
        to: (() => {
            const fromDate = new Date();
            const endOfMonthDate = endOfMonth(currentMonth);
            const minEndDate = addDays(fromDate, 14);
            return (isBefore(endOfMonthDate, minEndDate) ? minEndDate : endOfMonthDate).toISOString();
        })(),
        variantIds: variantIds,
        existingAppointmentId: appointmentId,
        ...(useRequestVariantIds ? { useRequestVariantIds: true } : {}),
        ...(practitionerId ? { practitionerId } : {}),
    }), [storeId, variantIds, currentMonth, appointmentId, practitionerId, useRequestVariantIds]);

    const availabilitiesQuery = trpc.publicAppointments.getAvailabilities.useQuery(queryParams, {
        placeholderData: keepPreviousData,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        staleTime: 1 * 60 * 1000,
        refetchInterval: 1 * 60 * 1000,
    });

    const timeslotsByDate: Record<string, { timeslot: Timeslot, priorityCategory: number }[]> = useMemo(() => {
        if (!availabilitiesQuery.data) {
            return {};
        }

        const rawAvailabilitiesData: { start: LocalDateTime, priorityCategory: number }[] = availabilitiesQuery.data.map((timeslot) => ({
            start: LocalDateTime.deserialize(timeslot?.startTimeSerialized),
            priorityCategory: timeslot.priorityCategory,
        }));
        const dateMap: Record<string, { timeslot: Timeslot, priorityCategory: number }[]> = {};

        const currentMonthStr = format(currentMonth, "yyyy-MM");

        rawAvailabilitiesData.filter((val) => {
            if (!val) { return false; }
            const dateKey = val.start.getDisplayDate();
            const monthStr = format(new Date(dateKey), "yyyy-MM");
            if (monthStr < currentMonthStr) { return false; }
            return true;
        }).forEach(timeslotData => {
            const dateKey = timeslotData.start.getDisplayDate();

            if (!(dateKey in dateMap)) {
                dateMap[dateKey] = [];
            }

            const timeslot = {
                startTime: timeslotData.start,
                startTimeStr: timeslotData.start.format("HH:mm")
            };

            dateMap[dateKey].push({
                timeslot: timeslot,
                priorityCategory: timeslotData.priorityCategory
            });
        });

        Object.keys(dateMap).forEach(dateKey => {
            dateMap[dateKey].sort((a, b) => {
                const aTime = a.timeslot.startTime.toUTC().getTime();
                const bTime = b.timeslot.startTime.toUTC().getTime();
                return aTime - bTime;
            });
        });

        return dateMap;
    }, [availabilitiesQuery.data, currentMonth]);

    const scrollToSelectedDate = (date: LocalDateTime) => {
        if (!dateScrollContainerRef.current) return;
        const container = dateScrollContainerRef.current;
        const dateKey = date.format('yyyy-MM-dd');
        const dateButton = container.querySelector(`[data-date="${dateKey}"]`) as HTMLElement;
        if (dateButton) {
            const containerRect = container.getBoundingClientRect();
            const buttonRect = dateButton.getBoundingClientRect();
            const containerCenter = containerRect.width / 2;
            const buttonCenter = buttonRect.left - containerRect.left + buttonRect.width / 2;
            const scrollOffset = buttonCenter - containerCenter;
            container.scrollTo({
                left: container.scrollLeft + scrollOffset,
                behavior: 'smooth'
            });
        }
    };

    const [hasTriedToSelectNextMonth, setHasTriedToSelectNextMonth] = useState<boolean>(false);
    useEffect(() => {
        if (!availabilitiesQuery?.data || hasTriedToSelectNextMonth) { return; }
        if (availabilitiesQuery.data.length === 0) {
            setCurrentMonth(addMonths(currentMonth, 1));
            setHasTriedToSelectNextMonth(true);
        }
    }, [availabilitiesQuery?.data]);

    const didPreselectRef = useRef(false);
    const preselectHoldRef = useRef(false);
    const skipFirstDayOverrideRef = useRef(false);
    const timeOfDayFor = (date: LocalDateTime): TimeOfDay => {
        const hour = date.getHours();
        if (hour < 12) return 'morning';
        if (hour < 16) return 'afternoon';
        return 'evening';
    };
    const findPreselectedSlot = () => {
        if (!preselectStartTime) return undefined;
        const slots = timeslotsByDate[preselectStartTime.getDisplayDate()];
        if (!slots) return undefined;
        const target = preselectStartTime.toUTC().getTime();
        return slots.find((slot) => Math.abs(slot.timeslot.startTime.toUTC().getTime() - target) < 60000);
    };

    const [hasInitialized, setHasInitialized] = useState<boolean>(false);
    useEffect(() => {
        if (hasInitialized || !timeslotsByDate || Object.keys(timeslotsByDate).length === 0) { return; }
        if (preselectStartTime) {
            setHasInitialized(true);
            return;
        }
        const sortedKeys = Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b));
        if (sortedKeys.length > 0 && timeslotsByDate[sortedKeys[0]].length > 0) {
            const firstAvailableTimeslot = timeslotsByDate[sortedKeys[0]][0];
            const firstAvailableDate = firstAvailableTimeslot.timeslot.startTime;
            setSelectedDate(firstAvailableDate);

            const hour = firstAvailableTimeslot.timeslot.startTime.getHours();
            if (hour < 12) { setSelectedTimeOfDay('morning'); }
            else if (hour >= 12 && hour < 16) { setSelectedTimeOfDay('afternoon'); }
            else { setSelectedTimeOfDay('evening'); }

            setHasInitialized(true);
            setTimeout(() => {
                const curMonthStr = format(currentMonth, "yyyy-MM");
                const firstTimeslotDate = new Date(firstAvailableTimeslot.timeslot.startTime.getDisplayDate());
                const firstTimeslotMonthStr = format(firstTimeslotDate, "yyyy-MM");
                if (curMonthStr !== firstTimeslotMonthStr) {
                    setCurrentMonth(firstTimeslotDate);
                }
                scrollToSelectedDate(firstAvailableDate);
            }, 100);
        }
    }, [timeslotsByDate]);

    useEffect(() => {
        if (!selectedDate || Object.keys(timeslotsByDate).length === 0) { return; }
        if (preselectStartTime && selectedDate.getDisplayDate() === preselectStartTime.getDisplayDate()) {
            setSelectedTimeOfDay(timeOfDayFor(preselectStartTime));
            return;
        }
        const timeslotDateKey = selectedDate.format('yyyy-MM-dd');
        const timeslotsOnDate = timeslotsByDate[timeslotDateKey];
        if (!timeslotsOnDate || timeslotsOnDate.length === 0) { return; }

        const firstAvailableTimeslot = timeslotsOnDate[0];
        const hour = firstAvailableTimeslot.timeslot.startTime.getHours();
        if (hour < 12) { setSelectedTimeOfDay('morning'); }
        else if (hour >= 12 && hour < 16) { setSelectedTimeOfDay('afternoon'); }
        else { setSelectedTimeOfDay('evening'); }
    }, [selectedDate]);

    useEffect(() => {
        if (
            timeslotViewMode === 'day-view' &&
            !availabilitiesQuery.isFetching &&
            selectedDate &&
            dateScrollContainerRef.current
        ) {
            const timeoutId = setTimeout(() => {
                scrollToSelectedDate(selectedDate);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [timeslotViewMode, availabilitiesQuery.isFetching, selectedDate]);

    const skipInitialMonthResetRef = useRef(!!preselectStartTime);
    useEffect(() => {
        if (skipInitialMonthResetRef.current) {
            skipInitialMonthResetRef.current = false;
            return;
        }
        setSelectedTimeOfDay("morning");
        setShowMoreTimeslots(false);
        setSelectedDate(null);
    }, [currentMonth]);

    useEffect(() => {
        if (!preselectStartTime || didPreselectRef.current) return;
        if (!availabilitiesQuery.isFetched || availabilitiesQuery.isFetching) return;

        const target = preselectStartTime.toUTC().getTime();
        const match = (availabilitiesQuery.data ?? []).find((slot) => {
            const start = LocalDateTime.deserialize(slot.startTimeSerialized);
            return Math.abs(start.toUTC().getTime() - target) < 60000;
        });
        didPreselectRef.current = true;

        if (!match) {
            setOriginalSlotUnavailable(true);
            return;
        }

        const start = LocalDateTime.deserialize(match.startTimeSerialized);
        preselectHoldRef.current = true;
        skipFirstDayOverrideRef.current = true;
        setOriginalSlotUnavailable(false);
        setSelectedDate(start);
        setSelectedTimeOfDay(timeOfDayFor(start));
        setSelectedTimeslot(start);
        onSelectDate(start);
        setTimeout(() => scrollToSelectedDate(start), 150);
    }, [availabilitiesQuery.data, availabilitiesQuery.isFetched, availabilitiesQuery.isFetching, preselectStartTime]);

    useEffect(() => {
        if (Object.keys(timeslotsByDate).length === 0) return;
        if (skipFirstDayOverrideRef.current) {
            skipFirstDayOverrideRef.current = false;
            return;
        }
        if (preselectStartTime && !didPreselectRef.current) return;
        if (preselectStartTime && selectedDate && selectedDate.getDisplayDate().slice(0, 7) === format(currentMonth, 'yyyy-MM')) {
            return;
        }
        const sortedKeys = Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b));
        if (sortedKeys.length > 0 && timeslotsByDate[sortedKeys[0]].length > 0) {
            const firstAvailableTimeslot = timeslotsByDate[sortedKeys[0]][0];
            setSelectedDate(firstAvailableTimeslot.timeslot.startTime);
        }
    }, [timeslotsByDate]);

    useEffect(() => {
        setShowMoreTimeslots(false);
        if (!selectedDate) {
            if (!preselectHoldRef.current) {
                setSelectedTimeslot(undefined);
                onSelectDate(undefined);
            }
            return;
        }
        preselectHoldRef.current = false;
        if (preselectStartTime && selectedDate.getDisplayDate() === preselectStartTime.getDisplayDate() && selectedTimeOfDay === timeOfDayFor(preselectStartTime)) {
            const target = preselectStartTime.toUTC().getTime();
            const match = (timeslotsByDate[preselectStartTime.getDisplayDate()] ?? []).find((slot) => Math.abs(slot.timeslot.startTime.toUTC().getTime() - target) < 60000);
            if (match) {
                setSelectedTimeslot(match.timeslot.startTime);
                onSelectDate(match.timeslot.startTime);
                return;
            }
        }
        setSelectedTimeslot(undefined);
        onSelectDate(undefined);
    }, [selectedDate, selectedTimeOfDay]);

    const monthDays = useMemo(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const today = new Date();
        return eachDayOfInterval({ start, end })
            .filter(day => !isBefore(day, today) || isSameDay(day, today))
            .map(day => format(day, 'yyyy-MM-dd'));
    }, [currentMonth]);

    const getAppointmentsCount = (date: LocalDateTime, timeOfDay?: TimeOfDay) => {
        const dateKey = date.getDisplayDate();
        const timeslots = timeslotsByDate[dateKey] || [];
        if (timeOfDay) {
            return timeslots.filter(slot => {
                const hour = slot.timeslot.startTime.getHours();
                switch (timeOfDay) {
                    case 'morning': return hour < 12;
                    case 'afternoon': return hour >= 12 && hour < 16;
                    case 'evening': return hour >= 16;
                }
            }).length;
        }
        return timeslots.length;
    };

    const getTimeslotScore = (date: LocalDateTime) => {
        const minutes = date.getMinutes();
        let score = 0;
        if (minutes % 5 !== 0) return -1;
        if (minutes === 0) score += 100;
        else if (minutes % 30 === 0) score += 80;
        else if (minutes % 15 === 0) score += 60;
        else if (minutes % 10 === 0) score += 40;
        else if (minutes % 5 === 0) score += 20;
        return score;
    };

    const selectBestTimeslots = (timeslots: { timeslot: Timeslot; priorityCategory: number; }[], maxCount: number) => {
        if (timeslots.length <= maxCount) return timeslots.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());

        const byCategory = new Map<number, typeof timeslots>();
        for (const slot of timeslots) {
            if (!byCategory.has(slot.priorityCategory)) {
                byCategory.set(slot.priorityCategory, []);
            }
            byCategory.get(slot.priorityCategory)!.push(slot);
        }

        for (const slots of byCategory.values()) {
            slots.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
        }

        const selected: typeof timeslots = [];
        let remaining = maxCount;

        const sortedCategories = [...byCategory.keys()].sort((a, b) => a - b);
        for (const category of sortedCategories) {
            if (remaining <= 0) break;
            const categorySlots = byCategory.get(category)!;

            if (categorySlots.length <= remaining) {
                selected.push(...categorySlots);
                remaining -= categorySlots.length;
            } else {
                const candidates = [...categorySlots];
                for (let i = 0; i < remaining; i++) {
                    if (selected.length === 0) {
                        const idx = Math.floor(candidates.length / 2);
                        selected.push(candidates.splice(idx, 1)[0]);
                    } else {
                        let bestIdx = 0;
                        let bestMinDist = -1;
                        let bestGapSize = -1;
                        const selectedTimes = selected.map(s => s.timeslot.startTime.toUTC().getTime()).sort((a, b) => a - b);
                        for (let c = 0; c < candidates.length; c++) {
                            const candidateTime = candidates[c].timeslot.startTime.toUTC().getTime();
                            let minDist = Infinity;
                            for (const t of selectedTimes) {
                                const dist = Math.abs(candidateTime - t);
                                if (dist < minDist) minDist = dist;
                            }
                            let gapSize = 0;
                            if (candidateTime <= selectedTimes[0]) {
                                gapSize = selectedTimes[0] - candidateTime;
                            } else if (candidateTime >= selectedTimes[selectedTimes.length - 1]) {
                                gapSize = candidateTime - selectedTimes[selectedTimes.length - 1];
                            } else {
                                for (let j = 0; j < selectedTimes.length - 1; j++) {
                                    if (candidateTime >= selectedTimes[j] && candidateTime <= selectedTimes[j + 1]) {
                                        gapSize = selectedTimes[j + 1] - selectedTimes[j];
                                        break;
                                    }
                                }
                            }
                            if (minDist > bestMinDist || (minDist === bestMinDist && gapSize > bestGapSize)) {
                                bestMinDist = minDist;
                                bestGapSize = gapSize;
                                bestIdx = c;
                            }
                        }
                        selected.push(candidates.splice(bestIdx, 1)[0]);
                    }
                }
                remaining = 0;
            }
        }

        return selected.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    };

    const getTimeslotsForSelectedDateTime = () => {
        if (!selectedDate) return [];
        const dateKey = selectedDate.format('yyyy-MM-dd');
        const dayTimeslots = timeslotsByDate[dateKey] || [];
        const filteredByTime = dayTimeslots.filter(slot => {
            const hour = slot.timeslot.startTime.getHours();
            switch (selectedTimeOfDay) {
                case 'morning': return hour < 12;
                case 'afternoon': return hour >= 12 && hour < 16;
                case 'evening': return hour >= 16;
                default: return false;
            }
        });
        return filteredByTime.filter(slot => getTimeslotScore(slot.timeslot.startTime) >= 0).sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    };

    const filteredTimeslots = getTimeslotsForSelectedDateTime();
    const bestTimeslots = showMoreTimeslots ? filteredTimeslots : selectBestTimeslots(filteredTimeslots, 4);
    const displayedTimeslots = (() => {
        if (!selectedTimeslot) return bestTimeslots;
        const selectedAt = selectedTimeslot.toUTC().getTime();
        if (bestTimeslots.some((slot) => slot.timeslot.startTime.toUTC().getTime() === selectedAt)) return bestTimeslots;
        const selectedSlot = filteredTimeslots.find((slot) => slot.timeslot.startTime.toUTC().getTime() === selectedAt);
        if (!selectedSlot) return bestTimeslots;
        return [...bestTimeslots, selectedSlot].sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    })();
    const hasMoreTimeslots = filteredTimeslots.length > 4;

    const canGoPrevious = !isBefore(subMonths(currentMonth, 1), startOfMonth(new Date()));

    const timeOfDayOptions = [
        { id: 'morning' as TimeOfDay, label: language === Language.GERMAN ? 'Vormittag' : 'Morning', icon: SunIcon, range: '8-12' },
        { id: 'afternoon' as TimeOfDay, label: language === Language.GERMAN ? 'Nachmittag' : 'Afternoon', icon: CloudIcon, range: '12-16' },
        { id: 'evening' as TimeOfDay, label: language === Language.GERMAN ? 'Abend' : 'Evening', icon: MoonIcon, range: '16-20' },
    ];

    const handleSelectTimeslot = (timeslot: LocalDateTime) => {
        setSelectedTimeslot(timeslot);
        onSelectDate(timeslot);
    };

    const sameTimeKept = !!preselectStartTime && !!selectedTimeslot
        && Math.abs(selectedTimeslot.toUTC().getTime() - preselectStartTime.toUTC().getTime()) < 60000;

    return (
        <div className="flex flex-col gap-3">
            {sameTimeKept && (
                <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                    <span className="text-sm text-green-800 font-light">{t('appointmentConfirmServicesQuestion')}</span>
                </div>
            )}
            {originalSlotUnavailable && (
                <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3">
                    <span className="text-sm text-yellow-700 font-light">{t('appointmentOriginalTimeUnavailable')}</span>
                </div>
            )}
            <div className="relative w-full bg-white p-4 rounded-[5px] flex flex-col gap-1 drop-shadow-md">
                <div className='w-full flex flex-row items-center justify-between'>
                    <span className='text-lg font-light text-burgundy'>
                        {language === Language.GERMAN ? 'Wähle Deinen Termin' : 'Select your appointment'}
                    </span>
                    <div className='flex flex-row items-center bg-burgundy/10 rounded-[5px] p-1'>
                        <button
                            className={`p-2 rounded-[5px] ${timeslotViewMode === 'day-view' ? 'bg-white' : ''}`}
                            onClick={() => setTimeslotViewMode('day-view')}
                        >
                            <Eye className="w-4 h-4 text-burgundy" />
                        </button>
                        <button
                            className={`p-2 rounded-[5px] ${timeslotViewMode === 'month-view' ? 'bg-white' : ''}`}
                            onClick={() => setTimeslotViewMode('month-view')}
                        >
                            <Calendar className="w-4 h-4 text-burgundy" />
                        </button>
                    </div>
                </div>

                <Separator className='my-2' />

                <div className='relative overflow-x-auto' ref={dateScrollContainerRef}>
                    {availabilitiesQuery.isFetching ? (
                        timeslotViewMode === 'day-view' ? (
                            <div className='flex gap-3 pb-4' style={{ minWidth: 'max-content' }}>
                                {[...Array(7)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-col items-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 animate-pulse"
                                    >
                                        <div className="w-8 h-5 bg-burgundy/20 rounded" />
                                        <div className="w-10 h-3 bg-burgundy/20 rounded mt-1" />
                                        <div className="w-2 h-2 rounded-full bg-burgundy/20 mt-1" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='flex flex-col gap-4 pb-4 overflow-x-scroll'>
                                <div className='flex flex-row items-center justify-between'>
                                    <div className="w-32 h-5 bg-burgundy/20 rounded animate-pulse" />
                                    <div className='flex flex-row items-center gap-2'>
                                        <div className="w-8 h-8 rounded-full bg-burgundy/20 animate-pulse" />
                                        <div className="w-8 h-8 rounded-full bg-burgundy/20 animate-pulse" />
                                    </div>
                                </div>
                                <div className='grid grid-cols-7 gap-0.5'>
                                    {(language === Language.GERMAN
                                        ? ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
                                        : ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
                                    ).map((day) => (
                                        <div key={day} className='flex items-center justify-center py-2'>
                                            <span className='text-xs font-semibold text-burgundy'>{day}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className='grid grid-cols-7 gap-0.5'>
                                    {[...Array(35)].map((_, i) => (
                                        <div key={i} className='flex flex-col items-center justify-center py-2 animate-pulse'>
                                            <div className="w-6 h-4 bg-burgundy/20 rounded" />
                                            <div className="w-1.5 h-1.5 rounded-full bg-burgundy/20 mt-1" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    ) : timeslotViewMode === 'day-view' ? (
                        <div className='flex gap-3 pb-4 w-max'>
                            {isAfter(startOfMonth(currentMonth), startOfMonth(new Date())) && (
                                <button
                                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                    className="flex flex-col items-center justify-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 hover:border-burgundy/50"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 text-burgundy" />
                                </button>
                            )}
                            {monthDays.map((dayStr, index) => {
                                const dayLocal = new LocalDateTime(`${dayStr} 10:00`, 'Europe/Berlin');
                                const appointmentsCount = getAppointmentsCount(dayLocal);
                                const isSelected = selectedDate && isSameDay(selectedDate.toUTC(), dayLocal.toUTC());
                                const isSunday = dayLocal.toUTC().getDay() === 0;
                                const isLastDayOfMonth = index === monthDays.length - 1;
                                const showWeekDivider = isSunday && !isLastDayOfMonth;

                                return (
                                    <div key={dayStr} className="flex items-center gap-3">
                                        <button
                                            data-date={dayStr}
                                            onClick={() => setSelectedDate(dayLocal)}
                                            className={`flex flex-col items-center gap-1 p-2 rounded-[5px] min-w-[40px] relative ${isSelected
                                                ? 'border-2 border-burgundy text-black font-medium pb-4'
                                                : 'border border-burgundy/30 hover:border-burgundy/50'
                                                }`}
                                        >
                                            <span className={`text-xs text-burgundy ${isSelected ? 'font-medium' : 'font-light'}`}>
                                                {dayLocal.format("EEEEEE", { locale: getDateLocaleByLocale(language) })}
                                            </span>
                                            <span className={`text-[13px] text-burgundy ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                                {dayLocal.format("d MMM", { locale: getDateLocaleByLocale(language) })}
                                            </span>
                                            {appointmentsCount > 0 ? (
                                                <div className='w-2 h-2 rounded-full bg-[#20A26C] mt-1' />
                                            ) : (
                                                <div className='w-2 h-2 rounded-full bg-light-gray mt-1' />
                                            )}
                                        </button>
                                        {showWeekDivider && (
                                            <div className='h-8 w-px bg-burgundy/20' />
                                        )}
                                    </div>
                                );
                            })}
                            <button
                                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                className="flex flex-col items-center justify-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 hover:border-burgundy/50"
                            >
                                <ChevronRightIcon className="h-5 w-5 text-burgundy" />
                            </button>
                        </div>
                    ) : (
                        <div className='flex flex-col gap-2'>
                            <div className='flex flex-row items-center justify-between'>
                                <span className='text-base font-medium text-burgundy'>
                                    {format(currentMonth, "MMMM yyyy", { locale: getDateLocaleByLocale(language) })}
                                </span>
                                <div className='flex flex-row items-center gap-2'>
                                    <button
                                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                        disabled={!canGoPrevious}
                                        className={`w-8 h-8 rounded-full border border-burgundy/30 flex items-center justify-center ${!canGoPrevious ? 'opacity-40 cursor-not-allowed' : 'hover:border-burgundy/50'}`}
                                    >
                                        <ChevronLeftIcon className="h-4 w-4 text-burgundy" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                        className="w-8 h-8 rounded-full border border-burgundy/30 flex items-center justify-center hover:border-burgundy/50"
                                    >
                                        <ChevronRightIcon className="h-4 w-4 text-burgundy" />
                                    </button>
                                </div>
                            </div>

                            <div className='grid grid-cols-7 gap-0.5'>
                                {(language === Language.GERMAN
                                    ? ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
                                    : ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
                                ).map((day) => (
                                    <div key={day} className='flex items-center justify-center py-1'>
                                        <span className='text-xs font-semibold text-burgundy'>{day}</span>
                                    </div>
                                ))}
                            </div>

                            <div className='grid grid-cols-7 justify-items-center'>
                                {(() => {
                                    const monthStart = startOfMonth(currentMonth);
                                    const monthEnd = endOfMonth(currentMonth);
                                    const today = new Date();

                                    let startDayOfWeek = monthStart.getDay() - 1;
                                    if (startDayOfWeek < 0) startDayOfWeek = 6;

                                    const daysInMonth = monthEnd.getDate();
                                    const cells = [];

                                    for (let i = 0; i < startDayOfWeek; i++) {
                                        cells.push(
                                            <div key={`empty-${i}`} className='flex items-center justify-center py-2' />
                                        );
                                    }

                                    for (let day = 1; day <= daysInMonth; day++) {
                                        const dayStr = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day), 'yyyy-MM-dd');
                                        const dayLocal = new LocalDateTime(`${dayStr} 10:00`, 'Europe/Berlin');
                                        const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

                                        const isPast = isBefore(dayDate, startOfDay(today));
                                        const appointmentsCount = getAppointmentsCount(dayLocal);
                                        const isSelected = selectedDate && isSameDay(selectedDate.toUTC(), dayLocal.toUTC());

                                        cells.push(
                                            <button
                                                key={dayStr}
                                                data-date={dayStr}
                                                onClick={() => !isPast && setSelectedDate(dayLocal)}
                                                disabled={isPast}
                                                className={`flex flex-col items-center justify-center px-3 py-2 rounded-[6px] border ${isPast ? 'opacity-60 cursor-not-allowed' : ''} ${isSelected ? 'border-burgundy' : 'border-transparent'}`}
                                            >
                                                <span className={`relative text-sm text-burgundy ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                                    {day}
                                                    {appointmentsCount === 0 && !isPast && (
                                                        <span className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-burgundy" />
                                                    )}
                                                </span>
                                                {!isPast && (
                                                    appointmentsCount > 0 ? (
                                                        <div className='w-1.5 h-1.5 rounded-full bg-[#20A26C] mt-0.5' />
                                                    ) : (
                                                        <div className='w-1.5 h-1.5 rounded-full bg-light-gray mt-0.5' />
                                                    )
                                                )}
                                            </button>
                                        );
                                    }

                                    return cells;
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedDate && (
                <div className='flex flex-col gap-4 bg-white mt-2 p-4 drop-shadow-md rounded-[5px]'>
                    <span className='text-base font-light text-burgundy'>
                        {language === Language.GERMAN ? 'Verfügbare Termine' : 'Available appointments'}
                    </span>

                    <div className='grid grid-cols-3 gap-3'>
                        {timeOfDayOptions.map((option) => {
                            const Icon = option.icon;
                            const isSelected = selectedTimeOfDay === option.id;
                            const hasTimeslots = timeslotsByDate[selectedDate.getDisplayDate()]?.some(slot => {
                                const hour = slot.timeslot.startTime.getHours();
                                switch (option.id) {
                                    case 'morning': return hour < 12;
                                    case 'afternoon': return hour >= 12 && hour < 16;
                                    case 'evening': return hour >= 16;
                                    default: return false;
                                }
                            }) || false;

                            return (
                                <button
                                    key={option.id}
                                    onClick={() => setSelectedTimeOfDay(option.id)}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-[5px] text-burgundy ${isSelected
                                        ? 'border-2 border-burgundy font-medium scale-110 md:scale-100'
                                        : 'border border-burgundy/50'
                                        }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span className='text-sm'>{option.range} Uhr</span>
                                    {hasTimeslots ? (
                                        <div className='w-2 h-2 rounded-full bg-[#20A26C]' />
                                    ) : (
                                        <span className='text-xs text-burgundy/60'>
                                            {language === Language.GERMAN ? 'Keine Termine' : 'No slots'}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <Separator />

                    {getAppointmentsCount(selectedDate, selectedTimeOfDay) > 0 ? (
                        <>
                            {filteredTimeslots.length > 0 && (
                                <div className='grid grid-cols-3 min-[350px]:grid-cols-4 gap-4'>
                                    {displayedTimeslots.map((timeslot, index) => {
                                        const isSelected = !selectedTimeslot ? false : selectedTimeslot.toUTC().getTime() === timeslot.timeslot.startTime.toUTC().getTime();

                                        return (
                                            <button
                                                key={`${timeslot.timeslot.startTime.getDisplayDateTime()}-${index}`}
                                                onClick={() => handleSelectTimeslot(timeslot.timeslot.startTime)}
                                                className={`w-full flex justify-center items-center px-2 py-2 rounded-[5px] bg-white ${isSelected
                                                    ? 'border-2 border-burgundy text-burgundy font-medium'
                                                    : 'border border-burgundy/50 text-burgundy/70 font-normal'
                                                    }`}
                                            >
                                                <span className='text-sm font-medium'>
                                                    {timeslot.timeslot.startTimeStr}
                                                </span>
                                            </button>
                                        );
                                    })}

                                    {hasMoreTimeslots && !showMoreTimeslots && (
                                        <button
                                            onClick={() => setShowMoreTimeslots(true)}
                                            className="col-span-2 w-full items-center p-3 text-burgundy rounded-lg transition-colors duration-200 underline text-left"
                                        >
                                            {language === Language.GERMAN ? 'Mehr anzeigen' : 'Show more'}
                                        </button>
                                    )}

                                    {showMoreTimeslots && hasMoreTimeslots && (
                                        <button
                                            onClick={() => setShowMoreTimeslots(false)}
                                            className="col-span-2 w-full items-center p-3 text-burgundy rounded-lg transition-colors duration-200 underline text-left"
                                        >
                                            {language === Language.GERMAN ? 'Weniger anzeigen' : 'Show less'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='px-2'>
                            <span className='text-burgundy font-light leading-normal text-sm'>
                                {language === Language.GERMAN
                                    ? 'Keine Termine zu dieser Tageszeit verfügbar. Bitte wähle eine andere Tageszeit oder einen anderen Tag.'
                                    : 'No appointments available at this time of day. Please select a different time or day.'}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {availabilitiesQuery.isError && (
                <div className="text-red-600 text-sm mt-2 font-light">
                    {t('appointmentAvailabilitiesError')}
                </div>
            )}
        </div>
    );
};

const shuffle = <T,>(items: T[]): T[] => {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
};

// On mobile the dialog sits higher so the reason dropdown has room to open below it
const ReasonSelect = <T extends CustomerCancellationReason>({
    value,
    onChange,
    options,
    placeholder,
}: {
    value: T | undefined;
    onChange: (value: T) => void;
    options: Array<[T, string]>;
    placeholder: string;
}) => (
    <Select value={value} onValueChange={(val) => onChange(val as T)}>
        <SelectTrigger className="w-full h-auto min-h-10 gap-2 px-3 py-2 border border-burgundy/20 rounded-[5px] shadow-none focus:ring-2 focus:ring-burgundy/50 text-burgundy text-base text-left whitespace-normal [&>span]:line-clamp-2 data-[placeholder]:text-burgundy/50">
            <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] max-h-[var(--radix-select-content-available-height)] overflow-y-auto text-burgundy">
            {options.map(([optionValue, label]) => {
                const Icon: LucideIcon = CUSTOMER_REASON_ICONS[optionValue];
                return (
                    <SelectItem key={optionValue} value={optionValue} className="py-1.5 text-xs leading-snug whitespace-normal focus:bg-burgundy/5 focus:text-burgundy">
                        <span className="flex items-start gap-2 text-left">
                            <Icon className="h-3.5 w-3.5 mt-px shrink-0 text-burgundy/70" />
                            <span className="min-w-0">{label}</span>
                        </span>
                    </SelectItem>
                );
            })}
        </SelectContent>
    </Select>
);

const FixedBottomBar = ({
    viewMode,
    onChangeAppointment,
    onChangeServices,
    onCancelAppointment,
    cancelFormOpen,
    onCancelFormOpenChange,
    onConfirmChange,
    onConfirmServices,
    onBackToDetails,
    onBackToServices,
    canBeModified,
    hasSelectedNewTime,
    requireMoveReason,
    isLoading
}: {
    viewMode: 'details' | 'changing' | 'services-time';
    onChangeAppointment: () => void;
    onChangeServices?: () => void;
    onCancelAppointment: (reasonCode: CustomerCancellationReason, note: string) => void;
    cancelFormOpen: boolean;
    onCancelFormOpenChange: (open: boolean) => void;
    onConfirmChange: () => void;
    onConfirmServices?: () => void;
    onBackToDetails: () => void;
    onBackToServices?: () => void;
    canBeModified: boolean;
    hasSelectedNewTime: boolean;
    requireMoveReason?: boolean;
    isLoading: boolean;
}) => {
    const { language, t } = useLanguageStore();

    const changeAlertDialogRef = useRef<HTMLButtonElement>(null);

    const [cancelReason, setCancelReason] = useState<CustomerCancellationReason | undefined>();
    const [note, setNote] = useState<string>("");

    // Shuffled once per mount so the order doesn't bias customers, but stays stable while choosing
    const cancelReasonOrder = useMemo(
        () => shuffle(Object.keys(CUSTOMER_CANCELLATION_REASON_LABELS) as CustomerCancellationReason[]),
        []
    );
    const getReasonLabel = (label: ReasonLabel) => language === Language.GERMAN ? label.de : label.en;
    const cancelReasonOptions = cancelReasonOrder.map((code): [CustomerCancellationReason, string] => [code, getReasonLabel(CUSTOMER_CANCELLATION_REASON_LABELS[code])]);

    const noteField = (
        <div className="space-y-2">
            <label className="text-sm font-normal text-burgundy">{t('appointmentNoteLabel')}</label>
            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('appointmentNotePlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-burgundy/20 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-burgundy/50 resize-none text-burgundy placeholder:text-burgundy/50"
            />
        </div>
    );

    if (viewMode === 'details') {
        if (!canBeModified) { return null; }

        return (
            <div className='w-full flex flex-col gap-3'>
                <PrimaryActionButton
                    onClick={onChangeAppointment}
                    label={t('appointmentChangeButton')}
                    leftIcon={<CalendarPlus className="w-4 h-4" />}
                    rightIcon={<ArrowRight />}
                />

                <Dialog open={cancelFormOpen} onOpenChange={onCancelFormOpenChange}>
                    <DialogTrigger disabled={isLoading}>
                        <button
                            onClick={() => { }}
                            className="flex flex-row items-center justify-center gap-2 w-full px-5 py-3 text-center text-red-600 font-medium border border-red-600 rounded-full hover:bg-red-50 transition-colors"
                        >
                            <CalendarX className='w-4 h-4' />
                            {t('appointmentCancelButton')}
                        </button>
                    </DialogTrigger>
                    <DialogContent className={REASON_DIALOG_CLASSES}>
                        <DialogHeader>
                            <DialogTitle className="text-burgundy">{t('appointmentAreYouSure')}</DialogTitle>
                            <DialogDescription className="text-burgundy/70">
                                {t('appointmentCancelConfirmQuestion')}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-normal text-burgundy">{t('appointmentReasonLabel')}</label>
                                <ReasonSelect
                                    value={cancelReason}
                                    onChange={setCancelReason}
                                    options={cancelReasonOptions}
                                    placeholder={t('appointmentReasonPlaceholder')}
                                />
                            </div>
                            {noteField}
                        </div>
                        <DialogFooter className='flex flex-col gap-2 sm:flex-col'>
                            <button
                                onClick={() => cancelReason && onCancelAppointment(cancelReason, note.trim())}
                                disabled={!cancelReason}
                                className="flex h-9 w-full flex-row items-center justify-center gap-2 px-5 text-center text-base text-red-600 font-medium border border-red-600 rounded-full hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <CalendarX className='w-4 h-4' />
                                {t('appointmentCancelButton')}
                            </button>
                            <DialogClose className={cn(buttonVariants({ variant: 'outline' }), DIALOG_CANCEL_BUTTON_CLASSES)}>{t('appointmentDialogCancel')}</DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    if (viewMode === 'services-time') {
        return (
            <div className='fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-4 p-6 pb-8 bg-light-burgundy'>
                <ScrollMoreHint />
                <Dialog>
                    <DialogTrigger ref={changeAlertDialogRef} disabled={!hasSelectedNewTime || isLoading}>
                        <PrimaryActionButton
                            onClick={() => { }}
                            label={requireMoveReason ? t('appointmentConfirmNewTimeButton') : t('appointmentConfirmServicesButton')}
                            disabled={!hasSelectedNewTime}
                            loading={isLoading}
                            rightIcon={<ArrowRight />}
                        />
                    </DialogTrigger>
                    <DialogContent className={REASON_DIALOG_CLASSES}>
                        <DialogHeader>
                            <DialogTitle className="text-burgundy">
                                {requireMoveReason ? t('appointmentAreYouSure') : t('appointmentConfirmServicesTitle')}
                            </DialogTitle>
                            <DialogDescription className="text-burgundy/70">
                                {requireMoveReason ? t('appointmentChangeConfirmQuestion') : t('appointmentConfirmServicesQuestion')}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className='flex items-center'>
                            <DialogClose className={cn(buttonVariants({ variant: 'outline' }), DIALOG_CANCEL_BUTTON_CLASSES)}>{t('appointmentDialogCancel')}</DialogClose>
                            <DialogClose asChild>
                                <PrimaryActionButton
                                    onClick={() => onConfirmServices?.()}
                                    label={requireMoveReason ? t('appointmentConfirmNewTime') : t('appointmentConfirmServices')}
                                    disabled={!hasSelectedNewTime}
                                    loading={isLoading}
                                />
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <button
                    onClick={onBackToServices}
                    className="w-full flex flex-row items-center justify-center px-5 py-3 cursor-pointer text-burgundy/70 font-medium border border-burgundy/20 rounded-full hover:bg-burgundy/5 transition-colors"
                >
                    {t('appointmentBackToServices')}
                </button>
            </div>
        );
    }

    // Changing mode
    return (
        <div className='fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-4 p-6 pb-8 bg-light-burgundy'>
            <ScrollMoreHint />
            <Dialog>
                <DialogTrigger ref={changeAlertDialogRef} disabled={!hasSelectedNewTime || isLoading}>
                    <PrimaryActionButton
                        onClick={() => { }}
                        label={t('appointmentConfirmNewTimeButton')}
                        disabled={!hasSelectedNewTime}
                        loading={isLoading}
                        rightIcon={<ArrowRight />}
                    />
                </DialogTrigger>
                <DialogContent className={REASON_DIALOG_CLASSES}>
                    <DialogHeader>
                        <DialogTitle className="text-burgundy">{t('appointmentAreYouSure')}</DialogTitle>
                        <DialogDescription className="text-burgundy/70">
                            {t('appointmentChangeConfirmQuestion')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className='flex items-center'>
                        <DialogClose className={cn(buttonVariants({ variant: 'outline' }), DIALOG_CANCEL_BUTTON_CLASSES)}>{t('appointmentDialogCancel')}</DialogClose>
                        <DialogClose asChild>
                            <PrimaryActionButton
                                onClick={() => onConfirmChange()}
                                label={t('appointmentConfirmNewTime')}
                                disabled={!hasSelectedNewTime}
                                loading={isLoading}
                            />
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <button
                onClick={onBackToDetails}
                className="w-full flex flex-row items-center justify-center px-5 py-3 cursor-pointer text-burgundy/70 font-medium border border-burgundy/20 rounded-full hover:bg-burgundy/5 transition-colors"
            >
                {t('appointmentBackToDetails')}
            </button>
        </div>
    );
};

const toBookingCartItems = (items: AppointmentCartItem[]): CartItem[] => items.map((item) => ({
    ...item,
    bookableItemDescriptionDe: '',
    bookableItemDescriptionEn: '',
    selectedVariantOptionLabelDe: '',
    selectedVariantOptionLabelEn: '',
    durationMinutes: item.durationMinutes,
    addOns: item.addOns.map((addOn) => ({ ...addOn, durationMinutes: addOn.durationMinutes })),
}));

const cartSignature = (items: CartItem[]) => JSON.stringify(
    items
        .map((item) => ({
            id: item.bookableItemId,
            variantId: item.selectedVariantId,
            bundleId: item.bundleId ?? null,
            redemption: !!item.isBundleRedemption,
            addOns: item.addOns.map((addOn) => addOn.addOnId).sort(),
        }))
        .sort((a, b) => `${a.id}:${a.variantId}`.localeCompare(`${b.id}:${b.variantId}`)),
);

const toBookableItems = (items: CartItem[]) => items.map((item) => ({
    bookableItemId: item.bookableItemId,
    bookableItemType: item.bookableItemType,
    treatmentVariantId: item.selectedVariantId,
    addOns: item.addOns.map((addOn) => ({
        addOnId: addOn.addOnId,
        variantId: addOn.variantId,
    })),
    bundleId: item.bundleId,
    isBundleRedemption: item.isBundleRedemption ?? false,
}));

type AppointmentExtras = {
    discountCode: string;
    onDiscountCodeChange: (code: string) => void;
    matchedDiscountCode: MatchedDiscountCode | null;
    onMatchedDiscountCodeChange: (matched: MatchedDiscountCode | null) => void;
    message: string;
    onMessageChange: (message: string) => void;
    paymentMethod: PaymentMethod;
    onPaymentMethodChange: (paymentMethod: PaymentMethod) => void;
    isWithinKlarnaDateRange: boolean;
    meetsKlarnaMinimumTotal: boolean;
    klarnaAuthorizedUntil: Date | null;
    readOnly: boolean;
};

const AppointmentDetails = ({
    appointment,
    cartItems,
    extras,
    extrasKey,
    notice,
    bottomActions,
    onChangeServices,
}: {
    appointment: Appointment;
    cartItems: CartItem[] | null;
    extras: AppointmentExtras;
    extrasKey: number;
    notice: ReactNode;
    bottomActions: ReactNode;
    onChangeServices?: () => void;
}) => {
    const { t } = useLanguageStore();

    const translateStatus = (status?: string | null): string => {
        if (!status) { return t('appointmentStatusUnknown'); }
        switch (status) {
            case 'SCHEDULED': return t('appointmentStatusScheduled');
            case 'REQUESTED': return t('appointmentStatusRequested');
            case 'CANCELLED': return t('appointmentStatusCancelled');
            case 'COMPLETED': return t('appointmentStatusCompleted');
            default: return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'SCHEDULED': return 'bg-green-100 text-green-800';
            case 'REQUESTED': return 'bg-yellow-100 text-yellow-800';
            case 'CANCELLED': return 'bg-red-100 text-red-800';
            case 'COMPLETED': return 'bg-blue-100 text-blue-800';
            default: return 'bg-burgundy/10 text-burgundy';
        }
    };

    const startTimeLocal = LocalDateTime.deserialize(appointment.startTimeLocalSerialized);
    const endTimeLocal = LocalDateTime.deserialize(appointment.endTimeLocalSerialized);
    const storeId = appointment.store.id === 'digital' ? undefined : appointment.store.id;

    return (
        <>
            <SummaryCard
                storeName={appointment.store.name}
                storeAddress={[appointment.store.address, appointment.store.zipCode].filter(Boolean).join(', ')}
                storeImage={appointment.store.images[0]}
                startTime={startTimeLocal}
                endTime={endTimeLocal}
                customerName={`${appointment.customer.firstName} ${appointment.customer.lastName}`}
            />

            <motion.div
                className='max-w-2xl mx-auto w-full px-6 pt-8 flex flex-col gap-10'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
            >
                {notice}

                <div className='-mb-6 flex flex-row items-center gap-2'>
                    <span className='text-sm font-normal text-burgundy'>{t('appointmentStatusTitle')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                        {translateStatus(appointment.status)}
                    </span>
                </div>

                {cartItems ? (
                    <ServicesList
                        cartItems={cartItems}
                        matchedDiscountCode={extras.matchedDiscountCode}
                        discountCode={extras.discountCode}
                        headerAction={onChangeServices ? (
                            <button
                                type="button"
                                onClick={onChangeServices}
                                className="shrink-0 inline-flex items-center gap-1 text-sm font-light text-burgundy"
                            >
                                <Pencil className="size-3.5" />
                                <span className="underline underline-offset-2">{t('appointmentChangeServicesButton')}</span>
                            </button>
                        ) : undefined}
                    >
                        <DiscountCodeSection
                            key={extrasKey}
                            discountCode={extras.discountCode}
                            onDiscountCodeChange={extras.onDiscountCodeChange}
                            matchedDiscountCode={extras.matchedDiscountCode}
                            onMatchedDiscountCodeChange={extras.onMatchedDiscountCodeChange}
                            cartItems={cartItems}
                            appointmentDate={startTimeLocal.toUTC()}
                            customerEmail={appointment.customer.email ?? undefined}
                            storeId={storeId}
                            readOnly={extras.readOnly}
                        />
                    </ServicesList>
                ) : (
                    <div className='flex flex-col gap-3'>
                        <span className='text-lg font-normal text-burgundy'>{t('appointmentTreatmentsTitle')}</span>
                        {appointment.variants.length > 0 ? (
                            <div className='flex flex-col gap-3'>
                                {appointment.variants.map((variant) => (
                                    <span key={variant.id} className='text-sm text-burgundy font-light'>{variant.name}</span>
                                ))}
                            </div>
                        ) : (
                            <span className='text-sm text-burgundy/70 font-light'>{t('appointmentNoTreatmentsListed')}</span>
                        )}
                    </div>
                )}

                {(!extras.readOnly || !!extras.klarnaAuthorizedUntil) && (
                    <div className='-mt-4'>
                        <PaymentMethodSection
                            value={extras.paymentMethod}
                            onChange={extras.onPaymentMethodChange}
                            isWithinDateRange={extras.isWithinKlarnaDateRange}
                            meetsMinimumTotal={extras.meetsKlarnaMinimumTotal}
                            readOnly={extras.readOnly || !!extras.klarnaAuthorizedUntil}
                            klarnaAuthorizedUntil={extras.klarnaAuthorizedUntil}
                        />
                    </div>
                )}

                {(!extras.readOnly || !!extras.message.trim()) && (
                    <div className='flex flex-col gap-3'>
                        <span className='text-sm font-normal text-burgundy'>{t('appointmentAdditionalOptions')}</span>
                        <MessageSection
                            key={extrasKey}
                            value={extras.message}
                            onChange={extras.onMessageChange}
                            readOnly={extras.readOnly}
                            commitOnSave
                        />
                    </div>
                )}

                {appointment.messageToCustomer && (
                    <div className='flex flex-col gap-3'>
                        <span className='text-lg font-normal text-burgundy'>{t('appointmentMessageFromClinic')}</span>
                        <p className='px-4 py-2.5 rounded-xl border border-burgundy/30 bg-white text-sm font-light text-burgundy whitespace-pre-wrap'>
                            {appointment.messageToCustomer}
                        </p>
                    </div>
                )}

                {bottomActions}
            </motion.div>
        </>
    );
};

const MAMA_FACIAL_BOOKABLE_ITEM_ID = '92dbd12f-d198-455e-ba89-f8f6d93947c3';
const BERATUNG_CATEGORY_ID = '8832839e-b008-4036-a358-b7dd26bec3b1';

const Appointment = () => {
    const { language, t } = useLanguageStore();

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = searchParams.get("id");
    const [viewMode, setViewMode] = useState<'details' | 'changing' | 'services' | 'services-time'>('details');
    const [draftCart, setDraftCart] = useState<CartItem[] | null>(null);
    const [mamaFacialItemId, setMamaFacialItemId] = useState<string | null>(null);
    const [consultationCategoryId, setConsultationCategoryId] = useState<string | null>(null);

    const [newSelectedDate, setNewSelectedDate] = useState<LocalDateTime | undefined>(undefined);

    const [cancelFormOpen, setCancelFormOpen] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [pendingCancel, setPendingCancel] = useState<{ reasonCode: CustomerCancellationReason; note: string } | null>(null);

    const appointmentQuery = trpc.publicAppointments.getAppointment.useQuery(
        {
            appointmentId: id || '',
        },
        {
            enabled: !!id,
            staleTime: 0 * 60 * 1000,
        }
    );

    const cancelAppointmentMutation = trpc.publicAppointments.cancelAppointment.useMutation({
        onSuccess: () => {
            appointmentQuery.refetch();
            toast.success(t('appointmentToastCancelled'));
        },
        onError: () => {
            toast.error(t('appointmentToastCancelError'));
        }
    });

    const rescheduleAppointmentMutation = trpc.publicAppointments.rescheduleAppointment.useMutation({
        onSuccess: () => {
            appointmentQuery.refetch();
            setViewMode('details');
            toast.success(t('appointmentToastChanged'));
        },
        onError: () => {
            toast.error(t('appointmentToastChangeError'));
        }
    });

    const modifyAppointmentServicesMutation = trpc.publicAppointments.modifyAppointmentServices.useMutation({
        onSuccess: () => {
            appointmentQuery.refetch();
            setDraftCart(null);
            setNewSelectedDate(undefined);
            setViewMode('details');
            toast.success(t('appointmentToastServicesChanged'));
        },
        onError: (error) => {
            toast.error(error.data?.code === 'CONFLICT' ? t('appointmentToastSlotUnavailable') : t('appointmentToastServicesError'));
        },
    });

    const { data: appointment } = appointmentQuery;

    const timeBeforeStartAllowedToModifyOrCancelHrs = 48;
    const startTimeDate = !!appointment ? LocalDateTime.deserialize(appointment.startTimeLocalSerialized) : LocalDateTime.fromUTCDate(new Date(), 'Europe/Berlin');
    const tooLateToChange = !(startTimeDate.toUTC().getTime() - new Date().getTime() > 1000 * 60 * 60 * timeBeforeStartAllowedToModifyOrCancelHrs);
    const isUpcomingOpenAppointment = !!appointment && (appointment.status === 'REQUESTED' || appointment.status === 'SCHEDULED') && startTimeDate.toUTC().getTime() > Date.now();
    const canBeModified = isUpcomingOpenAppointment && !tooLateToChange;

    // Discount code and message edits stay local until the save button is pressed
    const savedDiscountCode = (appointment?.couponCodes ?? '').trim();
    const savedMessage = (appointment?.messageFromCustomer ?? '').trim();
    const [pendingDiscountCode, setPendingDiscountCode] = useState('');
    const [pendingMatchedDiscountCode, setPendingMatchedDiscountCode] = useState<MatchedDiscountCode | null>(null);
    const [pendingMessage, setPendingMessage] = useState('');
    const [extrasKey, setExtrasKey] = useState(0);

    const klarnaAuthorizedUntil = useMemo(
        () => appointment?.klarnaAuthorization?.expiresAt ? new Date(appointment.klarnaAuthorization.expiresAt) : null,
        [appointment?.klarnaAuthorization?.expiresAt]
    );
    const savedPaymentMethod: PaymentMethod = appointment?.klarnaAuthorization ? 'klarna' : 'clinic';
    const [pendingPaymentMethod, setPendingPaymentMethod] = useState<PaymentMethod>('clinic');

    const resetExtras = useCallback(() => {
        setPendingDiscountCode(savedDiscountCode);
        setPendingMatchedDiscountCode(null);
        setPendingMessage(savedMessage);
        setPendingPaymentMethod(savedPaymentMethod);
        setExtrasKey((key) => key + 1);
    }, [savedDiscountCode, savedMessage, savedPaymentMethod]);

    useEffect(() => {
        resetExtras();
    }, [resetExtras]);

    const cartItems = useMemo(
        () => appointment?.cartItems ? toBookingCartItems(appointment.cartItems) : null,
        [appointment?.cartItems]
    );

    const isWithinKlarnaDateRange = differenceInCalendarDays(startTimeDate.toUTC(), new Date()) <= 27;
    const meetsKlarnaMinimumTotal = (cartItems
        ? getCartTotalAfterDiscount(cartItems, pendingMatchedDiscountCode)
        : (appointment?.bookedPrice ?? 0)) >= KLARNA_MIN_CART_TOTAL_EUR;
    const isKlarnaEligible = isWithinKlarnaDateRange && meetsKlarnaMinimumTotal;

    useEffect(() => {
        if (!isKlarnaEligible && savedPaymentMethod === 'clinic' && pendingPaymentMethod === 'klarna') {
            setPendingPaymentMethod('clinic');
        }
    }, [isKlarnaEligible, savedPaymentMethod, pendingPaymentMethod]);

    const isDiscountOrMessageDirty = pendingDiscountCode.trim() !== savedDiscountCode || pendingMessage.trim() !== savedMessage;
    const isExtrasDirty = isDiscountOrMessageDirty || pendingPaymentMethod !== savedPaymentMethod;

    const updateAppointmentExtrasMutation = trpc.publicAppointments.updateAppointmentExtras.useMutation();
    const createKlarnaAuthorizationMutation = trpc.publicPayments.createAppointmentKlarnaAuthorization.useMutation();
    const confirmKlarnaAuthorizationMutation = trpc.publicPayments.confirmAppointmentKlarnaAuthorization.useMutation();
    const cancelKlarnaCheckoutMutation = trpc.publicPayments.cancelKlarnaCheckout.useMutation();
    const [isStartingKlarna, setIsStartingKlarna] = useState(false);

    const startKlarnaAuthorization = async (appointmentId: string) => {
        const result = await createKlarnaAuthorizationMutation.mutateAsync({
            appointmentId,
            language: language === Language.GERMAN ? 'de' : 'en',
        });
        if (!result.success) {
            toast.error(result.error || t('appointmentToastKlarnaStartError'));
            return;
        }

        const stripe = await loadStripe(result.publishableKey);
        if (!stripe) {
            toast.error(t('appointmentToastKlarnaStartError'));
            return;
        }

        const { customer } = appointment!;
        const returnUrl = `${window.location.origin}/appointment?id=${encodeURIComponent(appointmentId)}&klarna_auth_id=${result.klarnaAuthorizationId}`;
        const { error } = await stripe.confirmKlarnaPayment(result.clientSecret, {
            payment_method: {
                billing_details: {
                    name: `${customer.firstName} ${customer.lastName}`.trim(),
                    email: customer.email ?? undefined,
                    phone: customer.phone ?? undefined,
                    address: { country: 'DE' },
                },
            },
            return_url: returnUrl,
        });

        if (error) {
            toast.error(error.message || t('appointmentToastKlarnaFailed'));
        }
    };

    const handleSaveExtras = async () => {
        if (!appointment) { return; }
        const wantsKlarna = pendingPaymentMethod === 'klarna' && savedPaymentMethod !== 'klarna';
        setIsStartingKlarna(wantsKlarna);
        try {
            if (isDiscountOrMessageDirty) {
                try {
                    await updateAppointmentExtrasMutation.mutateAsync({
                        appointmentId: appointment.id,
                        couponCode: pendingDiscountCode.trim(),
                        messageFromCustomer: pendingMessage.trim(),
                    });
                } catch {
                    toast.error(t('appointmentToastSaveError'));
                    return;
                }
                if (!wantsKlarna) {
                    await appointmentQuery.refetch();
                    toast.success(t('appointmentToastSaved'));
                }
            }
            if (wantsKlarna) {
                // Only resolves when Klarna could not be started; on success the browser is redirected
                await startKlarnaAuthorization(appointment.id);
            }
        } catch (e) {
            console.error("Failed to start Klarna authorization:", e);
            toast.error(t('appointmentToastKlarnaStartError'));
        } finally {
            setIsStartingKlarna(false);
            if (wantsKlarna && isDiscountOrMessageDirty) {
                appointmentQuery.refetch();
            }
        }
    };

    const handlePayWithKlarnaFromRetention = async () => {
        if (!appointment) { return; }
        setIsStartingKlarna(true);
        try {
            await startKlarnaAuthorization(appointment.id);
        } catch (e) {
            console.error("Failed to start Klarna authorization:", e);
            toast.error(t('appointmentToastKlarnaStartError'));
        } finally {
            setIsStartingKlarna(false);
        }
    };

    const isSavingExtras = updateAppointmentExtrasMutation.isPending || isStartingKlarna;

    const handledKlarnaReturnRef = useRef(false);
    useEffect(() => {
        const klarnaAuthId = searchParams.get('klarna_auth_id');
        if (!klarnaAuthId || !id || handledKlarnaReturnRef.current) { return; }
        handledKlarnaReturnRef.current = true;

        const redirectStatus = searchParams.get('redirect_status');
        setSearchParams({ id }, { replace: true });

        if (redirectStatus !== 'succeeded') {
            cancelKlarnaCheckoutMutation.mutate({ klarnaAuthorizationId: klarnaAuthId });
            toast.error(t('appointmentToastKlarnaFailed'));
            return;
        }

        confirmKlarnaAuthorizationMutation.mutate({ klarnaAuthorizationId: klarnaAuthId }, {
            onSuccess: (result) => {
                if (result.success) {
                    toast.success(t('appointmentToastKlarnaAuthorized'));
                } else {
                    toast.error(result.error || t('appointmentToastKlarnaFailed'));
                }
            },
            onError: () => {
                toast.error(t('appointmentToastKlarnaFailed'));
            },
            onSettled: () => {
                appointmentQuery.refetch();
            },
        });
    }, []);

    const originalVariantIds = useMemo(() => appointment?.variants?.map((v) => v.id) || [], [appointment?.variants]);

    const { sendEvent } = useGTM();
    const [preferredPractitionerId, setPreferredPractitionerId] = useState<number | null>(null);

    const pastPractitionersQuery = trpc.publicAppointments.getPastPractitionersForAppointment.useQuery(
        {
            appointmentId: id || '',
        },
        {
            enabled: !!id && canBeModified,
            staleTime: 10 * 60 * 1000,
        }
    );
    const pastPractitioners = pastPractitionersQuery.data ?? [];

    useEffect(() => {
        if (!preferredPractitionerId) {
            return;
        }
        if (pastPractitionersQuery.isFetched && !pastPractitioners.some((practitioner) => practitioner.id === preferredPractitionerId)) {
            setPreferredPractitionerId(null);
        }
    }, [pastPractitioners, pastPractitionersQuery.isFetched, preferredPractitionerId]);

    const handlePractitionerChange = (practitioner: PastPractitionerOption | null) => {
        const changedPractitioner = practitioner ?? pastPractitioners.find((option) => option.id === preferredPractitionerId);
        setPreferredPractitionerId(practitioner?.id ?? null);
        setNewSelectedDate(undefined);

        if (!changedPractitioner) {
            return;
        }

        sendEvent(practitioner ? GTMEvent.APPOINTMENT_MODIFICATION_PRACTITIONER_SELECTED : GTMEvent.APPOINTMENT_MODIFICATION_PRACTITIONER_DESELECTED, {
            entry_point: 'appointment_modification',
            deeplink_slug: undefined,
            deeplink_practitioner_id: undefined,
            deeplink_category_id: undefined,
            preferred_practitioner_id: practitioner?.id,
            preferred_practitioner_selected: !!practitioner,
            appointment_id: appointment?.id,
            practitioner_id: changedPractitioner.id,
            practitioner_name: formatPractitionerName(changedPractitioner),
            practitioner_type: changedPractitioner.type,
            branch: {
                id: appointment?.store?.id,
                name: appointment?.store?.name,
            },
            variant_ids: originalVariantIds,
        });
    };

    const handleGoToChange = () => {
        setNewSelectedDate(undefined);
        setViewMode('changing');
    };

    const handleGoToServices = () => {
        if (!appointment?.cartItems) return;
        setMamaFacialItemId(null);
        setConsultationCategoryId(null);
        setDraftCart(toBookingCartItems(appointment.cartItems));
        setNewSelectedDate(undefined);
        setViewMode('services');
    };

    const handleBookMamaFacial = () => {
        setCancelConfirmOpen(false);
        setPendingCancel(null);
        setConsultationCategoryId(null);
        setDraftCart([]);
        setNewSelectedDate(undefined);
        setMamaFacialItemId(MAMA_FACIAL_BOOKABLE_ITEM_ID);
        setViewMode('services');
    };

    const handleBookConsultation = () => {
        setCancelConfirmOpen(false);
        setPendingCancel(null);
        setMamaFacialItemId(null);
        setDraftCart([]);
        setNewSelectedDate(undefined);
        setConsultationCategoryId(BERATUNG_CATEGORY_ID);
        setViewMode('services');
    };

    const handleGoToServiceTime = () => {
        setNewSelectedDate(undefined);
        setViewMode('services-time');
    };

    const handleGoBackToDetails = () => {
        setMamaFacialItemId(null);
        setConsultationCategoryId(null);
        setViewMode('details');
    };

    const savedCartSignature = appointment?.cartItems ? cartSignature(toBookingCartItems(appointment.cartItems)) : '';
    const servicesChanged = !!draftCart && cartSignature(draftCart) !== savedCartSignature;
    const draftVariantIds = draftCart?.flatMap((item) => [item.selectedVariantId, ...item.addOns.map((addOn) => addOn.variantId)]) ?? [];
    const canChangeServices = canBeModified && typeof appointment?.store.id === 'number' && (appointment.cartItems?.length ?? 0) > 0;
    const startTimeChanged = !!newSelectedDate && Math.abs(newSelectedDate.toUTC().getTime() - startTimeDate.toUTC().getTime()) >= 60000;

    const handleConfirmChange = useCallback(async () => {
        if (!newSelectedDate || !appointment) {
            toast.error(t('appointmentToastSelectDateFirst'));
            return;
        }

        try {
            await rescheduleAppointmentMutation.mutateAsync({
                appointmentId: appointment.id,
                selectedStartTime: newSelectedDate.toJson(),
                ...(preferredPractitionerId ? { practitionerId: preferredPractitionerId } : {}),
            });
        } catch (error) {
            console.error("Failed to change appointment:", error);
        }
    }, [appointment, newSelectedDate, preferredPractitionerId, rescheduleAppointmentMutation, t]);

    const handleConfirmServices = useCallback(async () => {
        if (!newSelectedDate || !appointment || !draftCart || draftCart.length === 0) {
            toast.error(t('appointmentToastSelectDateFirst'));
            return;
        }

        try {
            await modifyAppointmentServicesMutation.mutateAsync({
                appointmentId: appointment.id,
                selectedStartTime: newSelectedDate.toJson(),
                bookableItems: toBookableItems(draftCart),
                ...(preferredPractitionerId ? { practitionerId: preferredPractitionerId } : {}),
            });
        } catch (error) {
            console.error("Failed to change appointment services:", error);
        }
    }, [appointment, draftCart, modifyAppointmentServicesMutation, newSelectedDate, preferredPractitionerId, t]);

    const handleCancelAppointment = (reasonCode: CustomerCancellationReason, note: string) => {
        if (!id) { return; }
        setPendingCancel({ reasonCode, note });
        setCancelFormOpen(false);
        setCancelConfirmOpen(true);
    };

    const handleConfirmCancelAppointment = async () => {
        if (!id || !pendingCancel) { return; }
        try {
            await cancelAppointmentMutation.mutateAsync({
                appointmentId: id,
                reasonCode: pendingCancel.reasonCode,
                note: pendingCancel.note,
            });
        } catch (error) {
            console.error("Failed to cancel appointment:", error);
        } finally {
            setCancelConfirmOpen(false);
            setPendingCancel(null);
        }
    };

    // Loading State
    if (appointmentQuery.isLoading || !id) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString={t('appointmentProgressAppointment')} />
                <div className='flex flex-col items-center justify-center flex-1 px-6'>
                    <Loader2 className='h-8 w-8 animate-spin text-burgundy' />
                    <span className='text-sm text-burgundy/70 font-light mt-4'>{t('appointmentLoading')}</span>
                </div>
            </motion.div>
        );
    }

    // Error State
    if (appointmentQuery.isError || !appointment) {
        return (
            <motion.div
                className='flex flex-col min-h-screen'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                <TopProgressBar percentageFilled={50} displayString={t('appointmentProgressAppointment')} onClickBackButton={() => navigate('/')} />
                <div className='flex flex-col items-center justify-center flex-1 px-6 text-center'>
                    <div className='flex flex-col gap-4'>
                        <span className='text-xl text-red-600 font-normal'>
                            {t('appointmentErrorTitle')}
                        </span>
                        <span className='text-sm text-burgundy/70 font-light'>
                            {appointmentQuery.isError
                                ? t('appointmentErrorLoading')
                                : t('appointmentErrorNotFound')}
                        </span>
                    </div>
                </div>
            </motion.div>
        );
    }

    if (viewMode === 'services' && draftCart && typeof appointment.store.id === 'number') {
        return (
            <ServiceSelection
                modify={{
                    storeId: appointment.store.id,
                    cartItems: draftCart,
                    onCartItemsChange: setDraftCart,
                    onContinue: handleGoToServiceTime,
                    onBack: handleGoBackToDetails,
                    continueDisabled: !servicesChanged,
                    initialBookableItemId: mamaFacialItemId ?? undefined,
                    onInitialItemConsumed: () => setMamaFacialItemId(null),
                    initialCategoryId: consultationCategoryId ?? undefined,
                    onInitialCategoryConsumed: () => setConsultationCategoryId(null),
                }}
            />
        );
    }

    const isServiceTime = viewMode === 'services-time';

    return (
        <motion.div
            className='flex flex-col min-h-screen'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <TopProgressBar
                percentageFilled={viewMode === 'details' ? 100 : 50}
                displayString={viewMode === 'details'
                    ? t('appointmentProgressDetails')
                    : isServiceTime
                        ? t('appointmentChangeServicesProgress')
                        : t('appointmentProgressChange')}
                onClickBackButton={viewMode === 'changing' ? handleGoBackToDetails : isServiceTime ? () => setViewMode('services') : undefined}
            />

            {viewMode === 'details' ? (
                <div className={`flex-1 ${isExtrasDirty ? 'pb-48' : 'pb-8'}`}>
                    <AppointmentDetails
                        appointment={appointment}
                        cartItems={cartItems}
                        extrasKey={extrasKey}
                        extras={{
                            discountCode: pendingDiscountCode,
                            onDiscountCodeChange: setPendingDiscountCode,
                            matchedDiscountCode: pendingMatchedDiscountCode,
                            onMatchedDiscountCodeChange: setPendingMatchedDiscountCode,
                            message: pendingMessage,
                            onMessageChange: setPendingMessage,
                            paymentMethod: pendingPaymentMethod,
                            onPaymentMethodChange: setPendingPaymentMethod,
                            isWithinKlarnaDateRange,
                            meetsKlarnaMinimumTotal,
                            klarnaAuthorizedUntil,
                            readOnly: !isUpcomingOpenAppointment,
                        }}
                        notice={!canBeModified && (
                            <div className='rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3'>
                                <span className='text-sm text-yellow-700 font-light'>
                                    {tooLateToChange
                                        ? t('appointmentTooLateNotice').replace('{hours}', String(timeBeforeStartAllowedToModifyOrCancelHrs))
                                        : t('appointmentCannotBeChanged')
                                    }
                                </span>
                            </div>
                        )}
                        onChangeServices={canChangeServices ? handleGoToServices : undefined}
                        bottomActions={!isExtrasDirty && canBeModified && (
                            <FixedBottomBar
                                viewMode="details"
                                onChangeAppointment={handleGoToChange}
                                onCancelAppointment={handleCancelAppointment}
                                cancelFormOpen={cancelFormOpen}
                                onCancelFormOpenChange={setCancelFormOpen}
                                onConfirmChange={handleConfirmChange}
                                onBackToDetails={handleGoBackToDetails}
                                canBeModified={canBeModified}
                                hasSelectedNewTime={!!newSelectedDate}
                                isLoading={rescheduleAppointmentMutation.isPending}
                            />
                        )}
                    />
                </div>
            ) : (
                <>
                    <div className='flex-1 overflow-y-auto px-4 pt-4 pb-48'>
                        <motion.div
                            className='flex flex-col gap-3 mb-6'
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.05, ease: "easeOut" }}
                        >
                            <h1 className='text-2xl text-burgundy text-center'>
                                {isServiceTime ? t('appointmentSelectTimeForServices') : t('appointmentSelectNewDate')}
                            </h1>
                        </motion.div>

                        <div className='w-full flex flex-col gap-2'>
                            {pastPractitioners.length > 0 && (
                                <PastPractitionerPickerRow
                                    practitioners={pastPractitioners}
                                    selectedPractitionerId={preferredPractitionerId}
                                    onChange={handlePractitionerChange}
                                />
                            )}
                            <ChangeAppointmentWidget
                                key={`${isServiceTime ? draftVariantIds.join(',') : 'time'}-${preferredPractitionerId ?? 'none'}`}
                                appointmentId={id}
                                storeId={appointment.store?.id || 'digital'}
                                onSelectDate={setNewSelectedDate}
                                variantIds={isServiceTime ? draftVariantIds : originalVariantIds}
                                practitionerId={preferredPractitionerId}
                                useRequestVariantIds={isServiceTime}
                                preselectStartTime={isServiceTime ? startTimeDate : undefined}
                            />
                        </div>
                    </div>

                    <FixedBottomBar
                        viewMode={isServiceTime ? 'services-time' : 'changing'}
                        onChangeAppointment={handleGoToChange}
                        onCancelAppointment={handleCancelAppointment}
                        cancelFormOpen={cancelFormOpen}
                        onCancelFormOpenChange={setCancelFormOpen}
                        onConfirmChange={isServiceTime ? () => { void handleConfirmServices(); } : handleConfirmChange}
                        onConfirmServices={() => { void handleConfirmServices(); }}
                        onBackToDetails={handleGoBackToDetails}
                        onBackToServices={() => setViewMode('services')}
                        canBeModified={canBeModified}
                        hasSelectedNewTime={!!newSelectedDate}
                        requireMoveReason={isServiceTime && startTimeChanged}
                        isLoading={isServiceTime ? modifyAppointmentServicesMutation.isPending : rescheduleAppointmentMutation.isPending}
                    />
                </>
            )}

            {viewMode === 'details' && (isExtrasDirty ? (
                <div className='fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 p-6 pb-8 bg-light-burgundy shadow-[0_-4px_12px_-6px_rgba(86,28,44,0.25)]'>
                    <ScrollMoreHint />
                    <PrimaryActionButton
                        onClick={() => { void handleSaveExtras(); }}
                        label={t('appointmentSaveChangesButton')}
                        loading={isSavingExtras}
                        rightIcon={<Check />}
                    />
                    <button
                        type='button'
                        onClick={resetExtras}
                        disabled={isSavingExtras}
                        className='self-center text-xs font-light text-burgundy underline underline-offset-2 hover:text-burgundy/70 disabled:opacity-50'
                    >
                        {t('appointmentDiscardChanges')}
                    </button>
                </div>
            ) : (
                <ScrollMoreHint className='fixed z-10 bottom-6' />
            ))}

            <CancelRetentionDialog
                open={cancelConfirmOpen}
                onOpenChange={(open) => {
                    if (!open && (cancelAppointmentMutation.isPending || isStartingKlarna)) { return; }
                    setCancelConfirmOpen(open);
                    if (!open) { setPendingCancel(null); }
                }}
                reasonCode={pendingCancel?.reasonCode ?? null}
                storePhone={appointment.store.contactPhone}
                storeEmail={appointment.store.contactEmail}
                isCancelling={cancelAppointmentMutation.isPending}
                onConfirmCancel={() => { void handleConfirmCancelAppointment(); }}
                onPayWithKlarna={!appointment.klarnaAuthorization
                    ? () => { void handlePayWithKlarnaFromRetention(); }
                    : undefined}
                klarnaMeetsMinimum={meetsKlarnaMinimumTotal}
                isStartingKlarna={isStartingKlarna}
                onBookMamaFacial={canChangeServices ? handleBookMamaFacial : undefined}
                onBookConsultation={canChangeServices ? handleBookConsultation : undefined}
                onReschedule={() => {
                    setCancelConfirmOpen(false);
                    setPendingCancel(null);
                    handleGoToChange();
                }}
            />
        </motion.div>
    );
};

export default Appointment;
